import { cloneElement, type ReactElement } from "react";

export type IconButtonVariant = "ghost" | "solid" | "signature" | "secondaryBordered";
export type IconButtonSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

interface IconButtonSharedProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children"> {
  size?: IconButtonSize;

  "aria-label": string;

  icon: ReactElement<{ size?: number }>;

  showDot?: boolean;
}

export type IconButtonProps =
  | (IconButtonSharedProps & {
      variant?: "ghost";
      /** True when rendered flat on the page instead of on an elevated surface. The page's own background is already surface-base, so Ghost's hover fill needs to be the darker interactive-hover token instead to stay visible. Mirrors ListItem's own `flat` prop. */
      flat?: boolean;
    })
  | (IconButtonSharedProps & { variant: "solid" | "signature" | "secondaryBordered" });

const SIZE_CLASSES: Record<IconButtonSize, string> = {
  xs: "size-4 rounded",
  sm: "size-6 rounded-md",
  md: "size-8 rounded-lg",
  lg: "size-9 rounded-lg",
  xl: "size-10 rounded-lg",
  "2xl": "size-12 rounded-lg",
};

const ICON_PIXEL_SIZE: Record<IconButtonSize, number> = {
  xs: 10,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
  "2xl": 24,
};

function ghostClasses(flat: boolean) {
  const hoverBg = flat ? "hover:before:bg-interactive-hover" : "hover:before:bg-surface-base";
  const expandedBg = flat ? "aria-expanded:before:bg-interactive-hover" : "aria-expanded:before:bg-surface-base";
  return [
    "text-content-secondary hover:text-content-primary",
    hoverBg,
    "aria-pressed:text-content-primary aria-pressed:before:bg-interactive-hover",
    "aria-expanded:text-content-primary",
    expandedBg,
  ].join(" ");
}

const STATIC_VARIANT_CLASSES: Record<Exclude<IconButtonVariant, "ghost">, string> = {
  solid: "text-content-inverse before:bg-content-primary hover:before:opacity-90",
  signature: "text-content-on-brand before:bg-brand-gradient hover:before:shadow-[var(--glow-brand)]",
  // Same recipe as Button's own secondaryBordered, plus a shadow: Button has no shadow on any
  // variant, but this one needs to read as present at rest with no hover, which Button never does.
  secondaryBordered:
    "text-content-primary before:border before:border-border-input before:bg-surface-primary before:shadow-md hover:before:border-content-tertiary hover:before:bg-surface-base",
};

export function IconButton(props: Readonly<IconButtonProps>) {
  const {
    variant = "ghost",
    size = "md",
    icon,
    showDot = false,
    disabled = false,
    type = "button",
    ...rest
  } = props as IconButtonSharedProps & { variant?: IconButtonVariant; flat?: boolean };
  const flat = variant === "ghost" ? Boolean(rest.flat) : false;
  const { flat: _flat, ...buttonProps } = rest;

  return (
    <button
      type={type}
      data-testid="icon-button"
      data-variant={variant}
      disabled={disabled}
      className={[
        "cursor-pointer",
        "relative isolate inline-flex items-center justify-center",
        "transition-colors duration-[180ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
        "before:absolute before:inset-0 before:-z-10 before:rounded-[inherit]",
        "before:transition-all before:duration-[180ms] before:ease-[cubic-bezier(0.16,1,0.3,1)]",
        "active:before:scale-[0.97]",
        "btn-focus-ring",
        "disabled:pointer-events-none disabled:opacity-50",
        SIZE_CLASSES[size],
        variant === "ghost" ? ghostClasses(flat) : STATIC_VARIANT_CLASSES[variant],
      ].join(" ")}
      {...buttonProps}
    >
      {cloneElement(icon, { size: ICON_PIXEL_SIZE[size] })}
      {showDot && (
        <span
          aria-hidden="true"
          className="absolute top-0.5 right-0.5 size-2 rounded-full bg-error"
          style={{ boxShadow: "0 0 0 2px var(--color-surface-base)" }}
        />
      )}
    </button>
  );
}
